import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { User, Channel, DirectMessage, Message, Thread, Workspace } from '@team-connect/types/team-chat';
import { supabase } from '@/lib/supabase';
import { useAuth } from './AuthContext';
import { useWorkspace } from './WorkspaceContext';
import { fetchWorkspaceMembers } from '@/lib/workspaceMembers';
import { safeLocalStorage } from '@/lib/storage-utils';
import { deleteFromR2 } from '@/lib/r2-storage';

interface TeamChatContextType {
  workspace: Workspace | null;
  currentUser: User | null;
  users: User[];
  channels: Channel[];
  directMessages: DirectMessage[];
  messages: Message[];
  threads: Thread[];
  activeChannelId: string | null;
  activeDmId: string | null;
  activeThreadId: string | null;
  setActiveChannel: (channelId: string | null) => void;
  setActiveDm: (dmId: string | null) => void;
  setActiveThread: (threadId: string | null) => void;
  sendMessage: (content: string, channelId?: string, dmId?: string, threadId?: string, alsoSendToChannel?: boolean, attachments?: { name: string, size: number, type: string, url: string }[], hiddenUrlPreviews?: string[]) => Promise<void>;
  joinChannel: (channelId: string) => Promise<void>;
  addMemberToChannel: (channelId: string, userId: string) => Promise<void>;
  createChannel: (name: string, description?: string, isPrivate?: boolean, initialMemberIds?: string[]) => Promise<void>;
  createDirectMessage: (targetUserId: string) => Promise<string | null>;
  updateChannel: (channelId: string, updates: Partial<Channel>) => Promise<void>;
  toggleReaction: (messageId: string, emoji: string) => Promise<void>;
  editMessage: (messageId: string, newContent: string) => Promise<void>;
  deleteMessage: (messageId: string) => Promise<void>;
  getUserById: (userId: string) => User | undefined;
  getChannelMessages: (channelId: string) => Message[];
  getDmMessages: (dmId: string) => Message[];
  getThreadMessages: (parentMessageId: string) => Message[];
  getDmParticipant: (dm: DirectMessage) => User | undefined;
  isChannelMember: (channelId: string) => boolean;
  leaveChannel: (channelId: string) => Promise<void>;
  deleteChannel: (channelId: string) => Promise<void>;
  preload: () => void;
  getTotalUnreadCount: () => number;
  // Egress optimizations & Presence
  hasMoreMessages: boolean;
  isLoadingMore: boolean;
  loadMoreMessages: () => Promise<void>;
  typingUsers: { userId: string; channelId: string }[];
  setTyping: (channelId: string, isTyping: boolean) => void;
  onlineUsers: string[];
}

const TeamChatContext = createContext<TeamChatContextType | undefined>(undefined);

// Helper to slugify names for URL
const slugify = (text: string) => text.toLowerCase().replace(/\s+/g, '-');

export const TeamChatProvider: React.FC<{ children: ReactNode; basePath?: string }> = ({ children, basePath = '/inbox' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const chatId = location.pathname.startsWith(`${basePath}/`)
    ? location.pathname.split(`${basePath}/`)[1]
    : null;
  const isInboxPage = location.pathname.startsWith(basePath);

  const { profile } = useAuth();
  const { currentWorkspace } = useWorkspace();

  const [users, setUsers] = useState<User[]>([]);
  const [channels, setChannels] = useState<Channel[]>([]);
  const [directMessages, setDirectMessages] = useState<DirectMessage[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [threads, setThreads] = useState<Thread[]>([]);
  const [hasTriggeredLoad, setHasTriggeredLoad] = useState(false);
  
  // Egress & Presence state
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [typingUsers, setTypingUsers] = useState<{ userId: string; channelId: string }[]>([]);
  const [onlineUsers, setOnlineUsers] = useState<string[]>([]);

  const preload = useCallback(() => {
    setHasTriggeredLoad(true);
  }, []);


  // Current user mapped to Team Chat User type
  const currentUser = useMemo<User | null>(() => {
    if (!profile) return null;
    return {
      id: profile.id,
      name: profile.email.split('@')[0],
      displayName: `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || profile.email.split('@')[0],
      avatar: profile.avatar_url || undefined,
      status: 'online',
    };
  }, [profile]);

  // Derived active states from URL
  const [activeChannelId, setActiveChannelId] = useState<string | null>(null);
  const [activeDmId, setActiveDmId] = useState<string | null>(null);
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);

  // Helper to get partner in DM
  const getDmParticipant = useCallback((dm: DirectMessage) => {
    if (!currentUser) return undefined;
    const otherUserId = dm.participants.find(id => id !== currentUser.id);
    return users.find(u => u.id === otherUserId);
  }, [users, currentUser]);

  // Load Workspace Members
  useEffect(() => {
    const loadMembers = async () => {
      if (!currentWorkspace?.id || (!isInboxPage && !hasTriggeredLoad)) return;
      const members = await fetchWorkspaceMembers(currentWorkspace.id);
      const mappedUsers: User[] = members.map(m => ({
        id: m.id,
        name: m.name.toLowerCase().replace(/\s+/g, ''),
        displayName: m.name,
        avatar: m.avatar,
        status: m.status === 'active' ? 'online' : 'offline',
      }));
      setUsers(mappedUsers);
    };
    loadMembers();
  }, [currentWorkspace?.id, isInboxPage, hasTriggeredLoad]);

  const loadChannels = useCallback(async () => {
    if (!currentWorkspace?.id || !currentUser || (!isInboxPage && !hasTriggeredLoad)) return;

    // 1. Get IDs of channels and their last_read_at
    const { data: membershipData } = await supabase
      .from('chat_channel_members')
      .select('channel_id, last_read_at')
      .eq('user_id', currentUser.id);

    const memberChannelIds = membershipData?.map(m => m.channel_id) || [];
    const lastReadMap = Object.fromEntries(membershipData?.map(m => [m.channel_id, m.last_read_at]) || []);

    // 2. Load public channels AND channels the user is a member of
    let query = supabase
      .from('chat_channels')
      .select(`
        *,
        chat_channel_members(user_id)
      `)
      .eq('workspace_id', currentWorkspace.id);

    if (memberChannelIds.length > 0) {
      query = query.or(`type.eq.public,id.in.(${memberChannelIds.join(',')})`);
    } else {
      query = query.eq('type', 'public');
    }

    const { data: channelsData, error } = await query;

    if (error) {
      console.error('Error loading channels:', error);
      return;
    }

    // 3. Fetch Last Message and Unread Count for each channel
    const channelsWithDetails = await Promise.all(channelsData.map(async (ch) => {
      // Get last message
      const { data: lastMsg } = await supabase
        .from('chat_messages')
        .select('content, created_at, user_id')
        .eq('channel_id', ch.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      // Get unread count
      const lastRead = lastReadMap[ch.id] || '1970-01-01';
      const { count: unreadCount } = await supabase
        .from('chat_messages')
        .select('*', { count: 'exact', head: true })
        .eq('channel_id', ch.id)
        .neq('user_id', currentUser.id)
        .gt('created_at', lastRead);

      // Prepend 'You: ' if last message is from current user
      const prefix = lastMsg?.user_id === currentUser.id ? 'You: ' : '';

      return {
        id: ch.id,
        unreadCount: unreadCount || 0,
        lastMessage: lastMsg ? prefix + lastMsg.content : undefined,
        lastMessageAt: lastMsg ? new Date(lastMsg.created_at) : undefined,
      };
    }));

    const detailMap = Object.fromEntries(channelsWithDetails.map(d => [d.id, d]));

    const mappedChannels: Channel[] = [];
    const mappedDms: DirectMessage[] = [];

    for (const ch of channelsData) {
      const details = detailMap[ch.id];
      if (ch.type === 'dm') {
        const isMember = ch.chat_channel_members.some((m: any) => m.user_id === currentUser.id);
        if (isMember) {
          mappedDms.push({
            id: ch.id,
            participants: ch.chat_channel_members.map((m: any) => m.user_id),
            unreadCount: details.unreadCount,
            lastMessage: details.lastMessage,
            lastMessageAt: details.lastMessageAt,
          });
        }
      } else {
        mappedChannels.push({
          id: ch.id,
          name: ch.name || 'Untitled',
          description: ch.description || undefined,
          isPrivate: ch.type === 'private',
          members: ch.chat_channel_members.map((m: any) => m.user_id),
          createdBy: ch.created_by,
          unreadCount: details.unreadCount,
          lastMessage: details.lastMessage,
          lastMessageAt: details.lastMessageAt,
          avatar: ch.avatar_url || undefined,
          avatarColor: ch.avatar_color || undefined,
        });
      }
    }

    setChannels(mappedChannels);
    setDirectMessages(mappedDms);
  }, [currentWorkspace?.id, currentUser?.id, isInboxPage, hasTriggeredLoad]);

  // Load Channels and DMs
  useEffect(() => {
    loadChannels();

    // Set up Realtime for channels
    const channelsSubscription = supabase
      .channel('chat_sidebar_realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'chat_channels' }, () => {
        loadChannels();
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'chat_channel_members' }, () => {
        loadChannels();
      })
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'chat_messages' }, (payload) => {
        const newMsg = payload.new;
        const targetId = newMsg.channel_id;
        
        // Update both lists with the last message and increment unread if not active
        const updateList = (list: any[]) => list.map(item => {
          if (item.id === targetId) {
            const isActive = targetId === activeChannelId || targetId === activeDmId;
            const isFromMe = newMsg.user_id === currentUser?.id;
            const prefix = isFromMe ? 'You: ' : '';
            
            return {
              ...item,
              lastMessage: prefix + newMsg.content,
              lastMessageAt: new Date(newMsg.created_at),
              unreadCount: (isActive || isFromMe) ? (isActive ? 0 : item.unreadCount) : (item.unreadCount || 0) + 1
            };
          }
          return item;
        });

        setChannels(prev => updateList(prev));
        setDirectMessages(prev => updateList(prev));
      })
      .subscribe();

    return () => {
      channelsSubscription.unsubscribe();
    };
  }, [loadChannels, activeChannelId, activeDmId]);

  // Mark as read whenever active chat changes
  useEffect(() => {
    if (currentUser && (activeChannelId || activeDmId)) {
      const targetId = (activeChannelId || activeDmId) as string;
      supabase
        .from('chat_channel_members')
        .update({ last_read_at: new Date().toISOString() })
        .eq('channel_id', targetId)
        .eq('user_id', currentUser.id)
        .then(() => {
          // Locally clear unread count for the active chat
          setChannels(prev => prev.map(c => c.id === targetId ? { ...c, unreadCount: 0 } : c));
          setDirectMessages(prev => prev.map(d => d.id === targetId ? { ...d, unreadCount: 0 } : d));
        });
    }
  }, [activeChannelId, activeDmId, currentUser?.id]);

  // Workspace Presence Sync - Global for the workspace
  useEffect(() => {
    if (!currentWorkspace?.id || !currentUser || (!isInboxPage && !hasTriggeredLoad)) return;

    const workspaceChannel = supabase.channel(`chat_workspace:${currentWorkspace.id}`)
      .on('presence', { event: 'sync' }, () => {
        const state = workspaceChannel.presenceState();
        const onlineIds = Object.keys(state).map(key => (state[key][0] as any).userId).sort();
        setOnlineUsers(prev => {
          const prevSorted = [...prev].sort();
          if (JSON.stringify(prevSorted) === JSON.stringify(onlineIds)) return prev;
          return onlineIds;
        });
      })
      .subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          await workspaceChannel.track({ 
            userId: currentUser.id, 
            online_at: new Date().toISOString() 
          });
        }
      });

    return () => {
      workspaceChannel.unsubscribe();
    };
  }, [currentWorkspace?.id, currentUser?.id, isInboxPage, hasTriggeredLoad]);

  // Load Messages & Setup Presence
  useEffect(() => {
    let currentSubscription: any = null;

    const loadMessages = async () => {
      // Clear messages when switching context
      setMessages([]);
      setThreads([]);
      setHasMoreMessages(false);
      
      const targetId = activeChannelId || activeDmId;
      if (!currentWorkspace?.id || (!isInboxPage && !hasTriggeredLoad) || !targetId) return;

      setIsLoadingMore(true);

      const { data, error } = await supabase
        .from('chat_messages')
        .select(`
          *,
          chat_reactions(emoji, user_id)
        `)
        .eq('channel_id', targetId)
        .order('created_at', { ascending: false })
        .limit(20);

      setIsLoadingMore(false);

      if (error) {
        console.error('Error loading messages:', error);
        return;
      }

      setHasMoreMessages(data.length === 20);

      // We fetch descending to get the latest 20, but need to display ascending
      const ascendingData = data.reverse();

      const mappedMessages: Message[] = ascendingData.map(msg => ({
        id: msg.id,
        channelId: msg.channel_id,
        userId: msg.user_id,
        content: msg.content,
        timestamp: new Date(msg.created_at),
        threadId: msg.parent_id || undefined,
        isThreadReply: !!msg.parent_id,
        threadParentId: msg.metadata?.thread_parent_id || undefined,
        isEdited: msg.is_edited,
        attachments: msg.metadata?.attachments || [],
        hiddenUrlPreviews: msg.metadata?.hiddenUrlPreviews || [],
        reactions: msg.chat_reactions?.reduce((acc: any[], r: any) => {
          const existing = acc.find(x => x.emoji === r.emoji);
          if (existing) {
            existing.userIds.push(r.user_id);
          } else {
            acc.push({ emoji: r.emoji, userIds: [r.user_id] });
          }
          return acc;
        }, []) || [],
      }));

      setMessages(mappedMessages);

      // Rebuild threads
      const threadMap = new Map<string, Thread>();
      mappedMessages.forEach(msg => {
        if (msg.threadId) {
          const thread = threadMap.get(msg.threadId) || {
            id: `thread-${msg.threadId}`,
            parentMessageId: msg.threadId,
            replyCount: 0,
            lastReplyAt: msg.timestamp,
            channelId: msg.channelId,
          };
          thread.replyCount++;
          if (msg.timestamp > thread.lastReplyAt) {
            thread.lastReplyAt = msg.timestamp;
          }
          threadMap.set(msg.threadId, thread);
        }
      });
      setThreads(Array.from(threadMap.values()));
    };

    loadMessages();

    // Set up Realtime for SPECIFIC active channel (Postgres + Presence + Broadcast)
    const targetId = activeChannelId || activeDmId;

    if (targetId && currentUser) {
      currentSubscription = supabase
        .channel(`chat_room:${targetId}`)
        // 1. Listen for new messages directly mapped into State (Zero DB refetch)
        .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'chat_messages', filter: `channel_id=eq.${targetId}` }, (payload) => {
          const newMsg = payload.new;
          const mappedMsg: Message = {
            id: newMsg.id,
            channelId: newMsg.channel_id,
            userId: newMsg.user_id,
            content: newMsg.content,
            timestamp: new Date(newMsg.created_at),
            threadId: newMsg.parent_id || undefined,
            isThreadReply: !!newMsg.parent_id,
            threadParentId: newMsg.metadata?.thread_parent_id || undefined,
            isEdited: newMsg.is_edited,
            attachments: newMsg.metadata?.attachments || [],
            hiddenUrlPreviews: newMsg.metadata?.hiddenUrlPreviews || [],
            reactions: [] // New msgs won't have reactions initially
          };
          setMessages(prev => [...prev, mappedMsg]);
        })
        // 2. Editing & Deletion & Reactions require refetches for simplicity right now
        .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'chat_messages', filter: `channel_id=eq.${targetId}` }, loadMessages)
        .on('postgres_changes', { event: 'DELETE', schema: 'public', table: 'chat_messages', filter: `channel_id=eq.${targetId}` }, loadMessages)
        .on('postgres_changes', { event: '*', schema: 'public', table: 'chat_reactions' }, loadMessages)
        
        // 3. Typers Broadcast (Ephemeral fast events)
        .on('broadcast', { event: 'typing' }, (payload) => {
           setTypingUsers(prev => {
             const { userId, isTyping } = payload.payload;
             if (isTyping) {
               if (!prev.find(t => t.userId === userId)) return [...prev, { userId, channelId: targetId }];
               return prev;
             } else {
               return prev.filter(t => t.userId !== userId);
             }
           });
        })
        
        .subscribe();
    }

    return () => {
      if (currentSubscription) {
        currentSubscription.unsubscribe();
      }
      setTypingUsers([]);
    };
  }, [currentWorkspace?.id, activeChannelId, activeDmId, isInboxPage, hasTriggeredLoad, currentUser]);

  const loadMoreMessages = useCallback(async () => {
    if (!messages.length || isLoadingMore || !hasMoreMessages) return;
    
    const targetId = activeChannelId || activeDmId;
    if (!targetId) return;

    setIsLoadingMore(true);
    
    const oldestMessageDate = messages[0].timestamp.toISOString();

    const { data, error } = await supabase
      .from('chat_messages')
      .select(`
        *,
        chat_reactions(emoji, user_id)
      `)
      .eq('channel_id', targetId)
      .lt('created_at', oldestMessageDate)
      .order('created_at', { ascending: false })
      .limit(20);

    setIsLoadingMore(false);

    if (error) {
      console.error('Error loading older messages:', error);
      return;
    }

    setHasMoreMessages(data.length === 20);

    const ascendingData = data.reverse();
    const olderMessages: Message[] = ascendingData.map(msg => ({
      id: msg.id,
      channelId: msg.channel_id,
      userId: msg.user_id,
      content: msg.content,
      timestamp: new Date(msg.created_at),
      threadId: msg.parent_id || undefined,
      isThreadReply: !!msg.parent_id,
      threadParentId: msg.metadata?.thread_parent_id || undefined,
      isEdited: msg.is_edited,
      attachments: msg.metadata?.attachments || [],
      hiddenUrlPreviews: msg.metadata?.hiddenUrlPreviews || [],
      reactions: msg.chat_reactions?.reduce((acc: any[], r: any) => {
          const existing = acc.find(x => x.emoji === r.emoji);
          if (existing) {
            existing.userIds.push(r.user_id);
          } else {
            acc.push({ emoji: r.emoji, userIds: [r.user_id] });
          }
          return acc;
        }, []) || [],
    }));

    setMessages(prev => [...olderMessages, ...prev]);
  }, [messages, isLoadingMore, hasMoreMessages, activeChannelId, activeDmId]);

  const setTyping = useCallback((channelId: string, isTyping: boolean) => {
    if (!currentUser) return;
    const channelName = `chat_room:${channelId}`;
    supabase.channel(channelName).send({
      type: 'broadcast',
      event: 'typing',
      payload: { userId: currentUser.id, isTyping }
    }).catch(err => console.error("Error broadcasting typing status", err));
  }, [currentUser]);

  // Effect to resolve URL chatId to internal IDs
  useEffect(() => {
    // 1. Handle "root" inbox access
    // On mobile: NEVER auto-navigate, always show sidebar first
    // On desktop: redirect to last active chat for better UX
    if (location.pathname === basePath && currentUser && currentWorkspace?.id) {
      const isMobile = window.innerWidth < 768;

      // On mobile, always show the sidebar (no auto-navigation)
      if (isMobile) {
        setActiveChannelId(null);
        setActiveDmId(null);
        return;
      }

      // On desktop, restore last active chat if available
      const lastActive = safeLocalStorage.getItem(`lastActiveChatId_${currentWorkspace.id}`);
      if (lastActive) {
        navigate(`${basePath}/${lastActive}`, { replace: true });
        return;
      }
    }

    if (!chatId || !currentUser) {
      setActiveChannelId(null);
      setActiveDmId(null);
      return;
    }

    // 2. Try to find matching channel by ID or Name
    const channel = channels.find(c =>
      c.id === chatId || slugify(c.name) === chatId.toLowerCase()
    );

    if (channel) {
      setActiveChannelId(channel.id);
      setActiveDmId(null);
      if (currentWorkspace?.id) {
        safeLocalStorage.setItem(`lastActiveChatId_${currentWorkspace.id}`, chatId);
      }
      return;
    }

    // 3. Try to find matching DM by ID or Participant Name
    const dm = directMessages.find(d => {
      if (d.id === chatId) return true;
      const participant = getDmParticipant(d);
      return participant && (
        slugify(participant.displayName) === chatId.toLowerCase() ||
        slugify(participant.name) === chatId.toLowerCase() ||
        participant.id === chatId
      );
    });

    if (dm) {
      setActiveDmId(dm.id);
      setActiveChannelId(null);
      if (currentWorkspace?.id) {
        safeLocalStorage.setItem(`lastActiveChatId_${currentWorkspace.id}`, chatId);
      }
      return;
    }

    setActiveChannelId(null);
    setActiveDmId(null);

  }, [chatId, channels, directMessages, currentUser, getDmParticipant, currentWorkspace?.id, navigate, isInboxPage, basePath]);

  const setActiveChannel = useCallback((channelId: string | null) => {
    if (channelId) {
      const channel = channels.find(c => c.id === channelId);
      if (channel) {
        navigate(`${basePath}/${slugify(channel.name)}`);
      } else {
        navigate(`${basePath}/${channelId}`);
      }
    } else {
      navigate(basePath);
    }
    setActiveThreadId(null);
  }, [navigate, channels, basePath]);

  const setActiveDm = useCallback((dmId: string | null) => {
    if (dmId) {
      const dm = directMessages.find(d => d.id === dmId);
      if (dm) {
        const participant = getDmParticipant(dm);
        if (participant) {
          navigate(`${basePath}/${slugify(participant.displayName)}`);
        } else {
          navigate(`${basePath}/${dmId}`);
        }
      } else {
        navigate(`${basePath}/${dmId}`);
      }
    } else {
      navigate(basePath);
    }
    setActiveThreadId(null);
  }, [navigate, directMessages, getDmParticipant, basePath]);

  const setActiveThread = useCallback((threadId: string | null) => {
    setActiveThreadId(threadId);
  }, []);

  const sendMessage = useCallback(async (content: string, channelId?: string, dmId?: string, threadId?: string, alsoSendToChannel?: boolean, attachments: { name: string, size: number, type: string, url: string }[] = [], hiddenUrlPreviews: string[] = []) => {
    if (!currentUser) return;

    const targetId = channelId || dmId;
    if (!targetId) return;

    const metadata: any = {};
    if (threadId) metadata.thread_parent_id = threadId;
    if (attachments.length > 0) metadata.attachments = attachments;
    if (hiddenUrlPreviews.length > 0) metadata.hiddenUrlPreviews = hiddenUrlPreviews;

    await supabase.from('chat_messages').insert({
      channel_id: targetId,
      user_id: currentUser.id,
      content,
      parent_id: threadId || null,
      metadata: Object.keys(metadata).length > 0 ? metadata : null
    });

    // If replying to a thread and invited to also send to channel
    if (threadId && alsoSendToChannel) {
      await supabase.from('chat_messages').insert({
        channel_id: targetId,
        user_id: currentUser.id,
        content,
        parent_id: null, // Top-level message
        metadata: {
          thread_parent_id: threadId,
          ...(attachments.length > 0 ? { attachments } : {}),
          ...(hiddenUrlPreviews.length > 0 ? { hiddenUrlPreviews } : {})
        }
      });
    }
  }, [currentUser]);

  const joinChannel = useCallback(async (channelId: string) => {
    if (!currentUser) return;

    // Check if already a member to avoid duplicate insert errors
    const channel = channels.find(c => c.id === channelId);
    if (channel?.members.includes(currentUser.id)) return;

    const { error } = await supabase.from('chat_channel_members').insert({
      channel_id: channelId,
      user_id: currentUser.id,
      role: 'member',
    });

    if (error) {
      console.error('Error joining channel:', error);
    }
  }, [currentUser, channels]);

  const addMemberToChannel = useCallback(async (channelId: string, userId: string) => {
    // Check if user is already a member to avoid duplicate insert errors
    const channel = channels.find(c => c.id === channelId);
    if (channel?.members.includes(userId)) return;

    const { error } = await supabase.from('chat_channel_members').insert({
      channel_id: channelId,
      user_id: userId,
      role: 'member',
    });

    if (error) {
      console.error('Error adding member to channel:', error);
      throw error;
    }
  }, [channels]);

  const createChannel = useCallback(async (name: string, description?: string, isPrivate = false, initialMemberIds: string[] = []) => {
    if (!currentWorkspace || !currentUser) return;

    const { data: channel, error } = await supabase
      .from('chat_channels')
      .insert({
        workspace_id: currentWorkspace.id,
        name,
        description,
        type: isPrivate ? 'private' : 'public',
        created_by: currentUser.id,
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating channel:', error);
      return;
    }

    // Add creator as owner
    const membersToAdd = [
      {
        channel_id: channel.id,
        user_id: currentUser.id,
        role: 'owner',
      }
    ];

    // Add other selected members
    initialMemberIds.forEach(id => {
      // Avoid adding creator twice if selected
      if (id !== currentUser.id) {
        membersToAdd.push({
          channel_id: channel.id,
          user_id: id,
          role: 'member',
        });
      }
    });

    await supabase.from('chat_channel_members').insert(membersToAdd);

    setActiveChannel(channel.id);
  }, [currentWorkspace, currentUser, setActiveChannel]);

  const createDirectMessage = useCallback(async (targetUserId: string) => {
    if (!currentWorkspace || !currentUser) return null;

    // Check if DM already exists
    const { data: existingDms } = await supabase
      .from('chat_channels')
      .select(`
        id,
        chat_channel_members!inner(user_id)
      `)
      .eq('workspace_id', currentWorkspace.id)
      .eq('type', 'dm');

    const dm = existingDms?.find(d =>
      d.chat_channel_members.length === 2 &&
      d.chat_channel_members.some((m: any) => m.user_id === targetUserId) &&
      d.chat_channel_members.some((m: any) => m.user_id === currentUser.id)
    );

    if (dm) return dm.id;

    // Create new DM channel
    const { data: newDm, error } = await supabase
      .from('chat_channels')
      .insert({
        workspace_id: currentWorkspace.id,
        type: 'dm',
        created_by: currentUser.id,
      })
      .select()
      .single();

    if (error) return null;

    // Add both participants
    await supabase.from('chat_channel_members').insert([
      { channel_id: newDm.id, user_id: currentUser.id },
      { channel_id: newDm.id, user_id: targetUserId }
    ]);

    return newDm.id;
  }, [currentWorkspace, currentUser]);

  const updateChannel = useCallback(async (channelId: string, updates: Partial<Channel>) => {
    const updatePayload: any = {
      updated_at: new Date().toISOString(),
    };

    if (updates.name !== undefined) updatePayload.name = updates.name;
    if (updates.description !== undefined) updatePayload.description = updates.description;
    if (updates.avatar !== undefined) updatePayload.avatar_url = updates.avatar;
    if (updates.avatarColor !== undefined) updatePayload.avatar_color = updates.avatarColor;

    // Map isPrivate to type
    if (updates.isPrivate !== undefined) {
      updatePayload.type = updates.isPrivate ? 'private' : 'public';
    }

    await supabase
      .from('chat_channels')
      .update(updatePayload)
      .eq('id', channelId);
  }, []);

  const toggleReaction = useCallback(async (messageId: string, emoji: string) => {
    if (!currentUser) return;

    const { data: existing } = await supabase
      .from('chat_reactions')
      .select('id')
      .eq('message_id', messageId)
      .eq('user_id', currentUser.id)
      .eq('emoji', emoji)
      .maybeSingle();

    if (existing) {
      await supabase.from('chat_reactions').delete().eq('id', existing.id);
    } else {
      await supabase.from('chat_reactions').insert({
        message_id: messageId,
        user_id: currentUser.id,
        emoji,
      });
    }
  }, [currentUser]);

  const editMessage = useCallback(async (messageId: string, newContent: string) => {
    if (!currentUser) return;
    await supabase
      .from('chat_messages')
      .update({
        content: newContent,
        is_edited: true,
        updated_at: new Date().toISOString(),
      })
      .eq('id', messageId)
      .eq('user_id', currentUser.id);
  }, [currentUser]);

  const deleteMessage = useCallback(async (messageId: string) => {
    if (!currentUser) return;
    
    const message = messages.find(m => m.id === messageId);
    
    // Optimistic UI update for instant feedback
    setMessages(prev => prev.filter(m => m.id !== messageId));

    if (message?.attachments) {
      // Fire and forget R2 deletion so it doesn't block database deletion
      Promise.all(message.attachments.map(att => att.url ? deleteFromR2(att.url) : Promise.resolve()))
        .catch(err => console.error('Failed to delete attachments from R2:', err));
    }

    await supabase
      .from('chat_messages')
      .delete()
      .eq('id', messageId)
      .eq('user_id', currentUser.id);
  }, [currentUser, messages]);

  const deleteChannel = useCallback(async (channelId: string) => {
    if (!currentUser) return;
    
    // Clean up all R2 attachments within the channel before deletion
    const channelMessages = messages.filter(m => m.channelId === channelId);
    for (const msg of channelMessages) {
      if (msg.attachments) {
        for (const att of msg.attachments) {
          if (att.url) {
            await deleteFromR2(att.url);
          }
        }
      }
    }

    const { error } = await supabase.from('chat_channels').delete().eq('id', channelId);
    if (!error) {
      setActiveChannel(null);
    }
  }, [currentUser, messages, setActiveChannel]);

  const leaveChannel = useCallback(async (channelId: string) => {
    if (!currentUser) return;
    const channel = channels.find(c => c.id === channelId);
    if (!channel) return;

    if (channel.createdBy === currentUser.id && channel.members.length > 1) {
      const { data: members } = await supabase
        .from('chat_channel_members')
        .select('user_id, role')
        .eq('channel_id', channelId)
        .neq('user_id', currentUser.id);

      if (members && members.length > 0) {
        let successor = members.find(m => m.role === 'owner');
        if (!successor) successor = members.find(m => m.role === 'admin');
        if (!successor) successor = members.find(m => m.role === 'manager');
        if (!successor) successor = members[0];

        if (successor) {
          await supabase
            .from('chat_channels')
            .update({ created_by: successor.user_id })
            .eq('id', channelId);

          if (successor.role !== 'owner') {
            await supabase
              .from('chat_channel_members')
              .update({ role: 'owner' })
              .eq('channel_id', channelId)
              .eq('user_id', successor.user_id);
          }
        }
      }
    }

    const { error } = await supabase
      .from('chat_channel_members')
      .delete()
      .eq('channel_id', channelId)
      .eq('user_id', currentUser.id);

    if (!error) {
      setActiveChannel(null);
    }
  }, [currentUser, channels, setActiveChannel]);

  const getUserById = useCallback((userId: string) => {
    return users.find(u => u.id === userId);
  }, [users]);

  const isChannelMember = useCallback((channelId: string) => {
    if (!currentUser) return false;
    const channel = channels.find(c => c.id === channelId);
    return channel ? channel.members.includes(currentUser.id) : false;
  }, [currentUser, channels]);

  const getChannelMessages = useCallback((channelId: string) => {
    return messages.filter(m => m.channelId === channelId && !m.isThreadReply);
  }, [messages]);

  const getDmMessages = useCallback((dmId: string) => {
    return messages.filter(m => m.channelId === dmId && !m.isThreadReply);
  }, [messages]);

  const getThreadMessages = useCallback((parentMessageId: string) => {
    return messages.filter(m => m.threadId === parentMessageId);
  }, [messages]);

  const getTotalUnreadCount = useCallback(() => {
    const channelUnread = channels.reduce((sum, channel) => sum + (channel.unreadCount || 0), 0);
    const dmUnread = directMessages.reduce((sum, dm) => sum + (dm.unreadCount || 0), 0);
    return channelUnread + dmUnread;
  }, [channels, directMessages]);

  const contextValue = useMemo<TeamChatContextType>(() => ({
    workspace: currentWorkspace ? { id: currentWorkspace.id, name: currentWorkspace.name, icon: currentWorkspace.logo } : null,
    currentUser,
    users,
    channels,
    directMessages,
    messages,
    threads,
    activeChannelId,
    activeDmId,
    activeThreadId,
    setActiveChannel,
    setActiveDm,
    setActiveThread,
    sendMessage,
    joinChannel,
    addMemberToChannel,
    createChannel,
    createDirectMessage,
    updateChannel,
    toggleReaction,
    editMessage,
    deleteMessage,
    getUserById,
    getChannelMessages,
    getDmMessages,
    getThreadMessages,
    getDmParticipant,
    isChannelMember,
    leaveChannel,
    deleteChannel,
    preload,
    getTotalUnreadCount,
    hasMoreMessages,
    isLoadingMore,
    loadMoreMessages,
    typingUsers,
    setTyping,
    onlineUsers,
  }), [
    currentWorkspace,
    currentUser,
    users,
    channels,
    directMessages,
    messages,
    threads,
    activeChannelId,
    activeDmId,
    activeThreadId,
    setActiveChannel,
    setActiveDm,
    setActiveThread,
    sendMessage,
    joinChannel,
    addMemberToChannel,
    createChannel,
    createDirectMessage,
    updateChannel,
    toggleReaction,
    editMessage,
    deleteMessage,
    getUserById,
    getChannelMessages,
    getDmMessages,
    getThreadMessages,
    getDmParticipant,
    isChannelMember,
    leaveChannel,
    deleteChannel,
    preload,
    getTotalUnreadCount,
    hasMoreMessages,
    isLoadingMore,
    loadMoreMessages,
    typingUsers,
    setTyping,
    onlineUsers,
  ]);

  return (
    <TeamChatContext.Provider value={contextValue}>
      {children}
    </TeamChatContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useTeamChat = () => {
  const context = useContext(TeamChatContext);
  if (!context) {
    throw new Error('useTeamChat must be used within a TeamChatProvider');
  }
  return context;
};
